import React from "react";
import { Link, useParams, useLocation } from "react-router";

import { Button } from "./ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "./ui/table";
import {
  Trash2,
  Pencil,
  GripVertical,
  Plus,
  Calendar,
  Activity,
  Star,
  Download,
} from "lucide-react";
import { kilnIsExportable } from "../helpers/exportHelpers";
import { glassImage } from "../helpers/logoHelpers";
import FiringGraph from "./FiringGraph";

import useFirebaseFiring from "../hooks/useFirebaseFiring";
import useFirebaseSegments from "../hooks/useFirebaseSegments";
import useFirebasePreferences from "../hooks/useFirebasePreferences";
import useFirebaseProjects from "../hooks/useFirebaseProjects";
import useFirebaseKilns from "../hooks/useFirebaseKilns";
import useSharedFirebaseFirings from "../hooks/useSharedFirebaseFirings";
import useSharedFirebaseSegments from "../hooks/useSharedFirebaseSegments";
import useSharedFirebaseProject from "../hooks/useSharedFirebaseProject";
import useFiringActions from "../hooks/useFiringActions";
import useSegmentActions from "../hooks/useSegmentActions";

import {
  convertDegreesInSegment,
  degreeText,
  parseRateValue,
  parseSegmentNumber,
} from "../helpers/unitHelpers";
import { convertSegmentsToTimedController } from "../helpers/timedController";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import _ from "lodash";
import PageHeader from "./PageHeader";

const microCapHead = "text-[11px] uppercase tracking-[.05em] font-semibold text-muted-foreground";

const FiringShowPage = () => {
  const { id, sharing_user_id: sharingUserId, shared_firing_id: sharedFiringId } = useParams();
  const location = useLocation();
  const isShared = !id;

  const firing = useFirebaseFiring(id!);
  const allSegments = useFirebaseSegments();
  const preferences = useFirebasePreferences();
  const projects = useFirebaseProjects();
  const kilns = useFirebaseKilns();

  const sharedFirings = useSharedFirebaseFirings(sharingUserId!);
  const sharedSegments = useSharedFirebaseSegments(sharingUserId!);

  const sharedFiring = sharedFirings?.[sharedFiringId!];
  const sharedProjectId = isShared ? (sharedFiring?.project_id || firing?.project_id) : null;
  const sharedProject = useSharedFirebaseProject(sharingUserId!, sharedProjectId as any);

  const { editFiring } = useFiringActions();
  const { editSegment } = useSegmentActions();

  const searchParams = new URLSearchParams(location.search);
  const shared_project_payload = searchParams.get("p");
  const readOnly = !!shared_project_payload || isShared;

  let currentFiring, segments, project, kiln;

  if (!isShared) {
    currentFiring = firing;
    segments = allSegments
      ? _.filter(allSegments, (segment) => segment.firing_id === id)
      : [];
    project = currentFiring?.project_id ? projects?.[currentFiring.project_id] : undefined;
    kiln = project?.kiln ? kilns?.[project.kiln] : undefined;
  } else {
    currentFiring = sharedFiring;
    segments = sharedSegments
      ? _.filter(sharedSegments, (segment) => segment?.firing_id === sharedFiringId)
      : [];
    project = sharedProject;
    kiln = {} as any;
  }

  const degrees = preferences && preferences.degrees ? preferences.degrees : "celsius";

  if (!currentFiring) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Firing"
          breadcrumbs={[{ label: "Projects", to: "/projects" }, { label: "Loading..." }]}
        />
        <div className="h-48 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  const reorderSegments = (data: any) => {
    data.forEach((element: any, index: number) => {
      if (element.order !== index) {
        element.order = index;
        editSegment(data[index].id, element, false);
      }
    });
  };

  const correct_degrees_segments = segments.map((segment) =>
    convertDegreesInSegment(segment, degrees)
  );
  const segments_array = Object.values(correct_degrees_segments);
  const sorted_segments_array = segments_array.sort((a, b) => a.order - b.order);

  const controller_sensitive_segments = convertSegmentsToTimedController(
    sorted_segments_array as any,
    kiln
  );

  const peakTemp =
    sorted_segments_array.length > 0
      ? Math.max(...sorted_segments_array.map((s) => Number(s.temperature)), 0)
      : 0;

  const totalMinutes = (() => {
    let elapsed = 0;
    let current = 20;
    sorted_segments_array.forEach((seg: any) => {
      const rate = parseRateValue(seg.rate);
      const temp = parseSegmentNumber(seg.temperature);
      if (rate > 0) {
        elapsed += Math.abs(temp - current) / (rate / 60);
      }
      elapsed += parseSegmentNumber(seg.hold);
      current = temp;
    });
    return elapsed;
  })();
  const totalDurationLabel = `${Math.floor(totalMinutes / 60)}h ${Math.round(
    totalMinutes % 60
  )}m`;

  const renderFavouriteButton = () => {
    const isFav = !!currentFiring!.favourite;
    return (
      <Button
        variant="outline"
        className="w-full justify-start"
        onClick={() => {
          editFiring(id!, { ...currentFiring!, favourite: !isFav }, false);
        }}
        disabled={readOnly}
      >
        <Star
          className={`mr-1.5 h-3.5 w-3.5 ${isFav ? "fill-yellow-400 text-yellow-400" : ""}`}
        />
        {isFav ? "Unmark as Favourite" : "Mark as Favourite"}
      </Button>
    );
  };

  const onBeforeDragStart = (start: any) => {
    const row = document.querySelector(
      `[data-rfd-draggable-id="${start.draggableId}"]`
    );
    if (!row) return;
    const cells = row.querySelectorAll("td");
    cells.forEach((cell: any) => {
      cell.style.width = `${cell.offsetWidth}px`;
    });
  };

  const clearCellWidths = () => {
    document
      .querySelectorAll("[data-rfd-draggable-id] td")
      .forEach((cell: any) => {
        cell.style.width = "";
      });
  };

  const getItemStyle = (isDragging: any, draggableStyle: any) => ({
    display: isDragging ? "table" : undefined,
    background: isDragging ? "var(--color-muted)" : undefined,
    ...draggableStyle,
  });

  const reorderList = (list: any, startIndex: any, endIndex: any) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result: any) => {
    if (!result.destination || readOnly) {
      return;
    }
    const new_sorted_segments = reorderList(
      sorted_segments_array,
      result.source.index,
      result.destination.index
    );
    reorderSegments(new_sorted_segments);
    clearCellWidths();
  };

  const headerActions = !readOnly ? (
    <Link to={`/firings/edit/${id}`}>
      <Button variant="outline" size="sm">
        <Pencil className="mr-1 h-4 w-4" />
        Edit
      </Button>
    </Link>
  ) : undefined;

  const breadcrumbs = project
    ? [
        { label: "Projects", to: isShared ? undefined : "/projects" },
        {
          label: project.name || "Project",
          to: isShared ? undefined : `/projects/${currentFiring.project_id}`,
        },
        { label: currentFiring.name || "Firing" },
      ]
    : [{ label: currentFiring.name || "Firing" }];

  const nextSegmentOrder =
    Math.max(...sorted_segments_array.map((s) => s.order), 0) + 1;

  return (
    <DragDropContext onBeforeDragStart={onBeforeDragStart} onDragEnd={onDragEnd}>
      <div className="space-y-6">
        <PageHeader
          title={currentFiring.name || "Firing"}
          breadcrumbs={breadcrumbs as any}
          actions={headerActions}
        />

        <div className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-6">
          {/* LEFT PANE: graph + segments */}
          <div className="space-y-6 min-w-0">
            {sorted_segments_array.length > 0 && (
              <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
                <div className="flex items-center gap-2.5 border-b py-3.5 px-[18px]">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-[15px] font-semibold">Firing graph</h3>
                  <span className="ml-auto text-xs text-muted-foreground font-mono tabular-nums">
                    {totalDurationLabel} · peak {peakTemp}
                    {degreeText(degrees)}
                  </span>
                </div>
                <div className="p-[18px]">
                  <FiringGraph
                    sortedSegments={sorted_segments_array}
                    degrees={degrees}
                    project={project}
                  />
                </div>
              </div>
            )}

            <section>
              <div className="flex items-center justify-between mb-2.5">
                <h3 className="text-[15px] font-semibold">Segments</h3>
                {sorted_segments_array.length > 0 && (
                  <span className="text-xs text-muted-foreground font-mono tabular-nums">
                    {sorted_segments_array.length} segment
                    {sorted_segments_array.length === 1 ? "" : "s"} ·{" "}
                    {totalDurationLabel} total
                  </span>
                )}
              </div>

              <div className="rounded-[10px] border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[28px]" />
                      <TableHead className={microCapHead}>Name</TableHead>
                      <TableHead className={microCapHead}>
                        {`Rate (${degreeText(degrees)}/hr)`}
                      </TableHead>
                      <TableHead className={microCapHead}>
                        {kiln?.timed_controller
                          ? `Time (hh:mm)`
                          : `Target (${degreeText(degrees)})`}
                      </TableHead>
                      <TableHead className={microCapHead}>
                        Hold (mins)
                      </TableHead>
                      <TableHead className={`${microCapHead} text-right`}>
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <Droppable droppableId="segments_table">
                    {(provided) => (
                      <TableBody
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {controller_sensitive_segments.map((segment, index) => (
                          <Draggable
                            key={segment.id}
                            draggableId={segment.id!}
                            index={index}
                            isDragDisabled={readOnly}
                          >
                            {(provided, snapshot) => (
                              <TableRow
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={getItemStyle(
                                  snapshot.isDragging,
                                  provided.draggableProps.style
                                )}
                              >
                                <TableCell
                                  className="w-[28px] text-muted-foreground cursor-grab hover:text-foreground"
                                  {...provided.dragHandleProps}
                                >
                                  <GripVertical className="h-4 w-4" />
                                </TableCell>
                                <TableCell>{segment.name}</TableCell>
                                <TableCell className="font-mono tabular-nums">
                                  {segment.rate}
                                </TableCell>
                                <TableCell className="font-mono tabular-nums">
                                  {segment.temperature}
                                </TableCell>
                                <TableCell className="font-mono tabular-nums">
                                  {segment.hold}
                                </TableCell>
                                <TableCell className="text-right">
                                  {!readOnly && (
                                    <div className="flex justify-end gap-0.5">
                                      <Link to={`/segments/edit/${segment.id}`}>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-7 w-7"
                                        >
                                          <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                      </Link>
                                      <Link to={`/segments/delete/${segment.id}`}>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-7 w-7 text-destructive hover:text-destructive"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                      </Link>
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </Table>

                <div className="flex justify-between items-center px-3.5 py-2.5 bg-muted border-t text-sm">
                  {!readOnly ? (
                    <Link
                      to={`/new_segment/${id}/${nextSegmentOrder}`}
                      className="text-primary font-medium inline-flex items-center gap-1 hover:underline"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add Segment
                    </Link>
                  ) : (
                    <span />
                  )}
                  {sorted_segments_array.length > 0 && (
                    <div className="flex gap-4 text-muted-foreground">
                      <span>
                        Peak{" "}
                        <strong className="text-foreground font-mono tabular-nums">
                          {peakTemp}
                          {degreeText(degrees)}
                        </strong>
                      </span>
                      <span>
                        Total{" "}
                        <strong className="text-foreground font-mono tabular-nums">
                          {totalDurationLabel}
                        </strong>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>

          {/* RIGHT PANE: meta + actions */}
          <div className="space-y-6 min-w-0">
            <div className="bg-card border border-border rounded-xl p-[18px] shadow-sm">
              <h2 className="text-lg font-bold tracking-tight">
                {currentFiring.name}
              </h2>
              {currentFiring.date && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3.5 mt-0.5">
                  <Calendar className="h-3 w-3" />
                  Fired {new Date(currentFiring.date).toDateString()}
                </div>
              )}
              {currentFiring.notes && (
                <div className="text-sm leading-relaxed p-2.5 bg-muted rounded-lg mb-3.5">
                  {currentFiring.notes}
                </div>
              )}

              {project && !isShared && (
                <div className="flex justify-between items-center text-sm py-2 border-b border-dashed border-border">
                  <span className="text-muted-foreground">Project</span>
                  <Link
                    to={`/projects/${currentFiring.project_id}`}
                    className="text-primary font-medium hover:underline"
                  >
                    {project?.name}
                  </Link>
                </div>
              )}
              {kiln?.name && (
                <div className="flex justify-between items-center text-sm py-2 border-b border-dashed border-border">
                  <span className="text-muted-foreground">Kiln</span>
                  <span className="font-medium">{kiln.name}</span>
                </div>
              )}
              {project?.glass && (
                <div className="flex justify-between items-center text-sm py-2 border-b border-dashed border-border">
                  <span className="text-muted-foreground">Glass</span>
                  <span className="font-medium inline-flex items-center gap-1.5">
                    <img
                      src={glassImage(project.glass)}
                      alt=""
                      className="h-4 w-4 rounded-full object-contain"
                    />
                    {project.glass}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center text-sm py-2 border-b border-dashed border-border last:border-0">
                <span className="text-muted-foreground">Segments</span>
                <span className="font-medium font-mono tabular-nums">
                  {sorted_segments_array.length}
                </span>
              </div>
              {sorted_segments_array.length > 0 && (
                <div className="flex justify-between items-center text-sm py-2 border-b border-dashed border-border last:border-0">
                  <span className="text-muted-foreground">Peak temp</span>
                  <span className="font-medium font-mono tabular-nums">
                    {peakTemp}
                    {degreeText(degrees)}
                  </span>
                </div>
              )}
              {sorted_segments_array.length > 0 && (
                <div className="flex justify-between items-center text-sm py-2 border-b border-dashed border-border last:border-0">
                  <span className="text-muted-foreground">Total duration</span>
                  <span className="font-medium font-mono tabular-nums">
                    {totalDurationLabel}
                  </span>
                </div>
              )}
            </div>

            {!readOnly && (
              <div>
                <h3 className="text-[15px] font-semibold mb-2.5">
                  Firing actions
                </h3>

                {currentFiring.favourite && (
                  <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm font-medium mb-2.5">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <span>
                      Favourite — available when creating new firings.
                    </span>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  {renderFavouriteButton()}
                  {kilnIsExportable(kiln) && (
                    <Link to={`/firing/export/${kiln?.id}/${id}/`}>
                      <Button
                        variant="outline"
                        className="w-full justify-start"
                      >
                        <Download className="mr-1.5 h-3.5 w-3.5" />
                        Export to Kiln Controller
                      </Button>
                    </Link>
                  )}
                  {segments_array.length === 0 && (
                    <Link to={`/firing/delete/${id}`}>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-destructive border-destructive/30 hover:bg-destructive/5 hover:text-destructive"
                      >
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                        Delete Firing
                      </Button>
                    </Link>
                  )}
                </div>

                <p className="text-[11px] text-muted-foreground mt-2.5 leading-relaxed">
                  Delete is only available once all segments have been removed.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DragDropContext>
  );
};

export default FiringShowPage;
